/**
 * @krisspy-file
 * @type component
 * @name "AssignIdeaModal"
 * @description "Mobilní modální okno pro přiřazení nápadu k touchpointu (fáze + typ)"
 */

import React, { useState, useEffect } from 'react';
import { X, ArrowRight } from 'lucide-react';

interface Phase {
  id: string;
  name: string;
  order: number;
}

interface AssignIdeaModalProps {
  isOpen: boolean;
  onClose: () => void;
  ideaTitle: string;
  phases: Phase[];
  onAssign: (params: { phaseId: string; type: 'digital' | 'physical' }) => void;
}

const AssignIdeaModal: React.FC<AssignIdeaModalProps> = ({
  isOpen,
  onClose,
  ideaTitle,
  phases,
  onAssign,
}) => {
  const [selectedPhaseId, setSelectedPhaseId] = useState('');
  const [selectedType, setSelectedType] = useState<'digital' | 'physical'>('digital');
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setSelectedPhaseId(phases[0]?.id ?? '');
      setSelectedType('digital');
      setError('');
    }
  }, [isOpen, phases]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPhaseId) {
      setError('Vyberte fázi');
      return;
    }
    onAssign({ phaseId: selectedPhaseId, type: selectedType });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 sm:p-6">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Sheet / Dialog */}
      <div className="relative bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
        {/* Handle bar (mobile) */}
        <div className="sm:hidden flex justify-center pt-3 pb-1">
          <div className="w-8 h-1 rounded-full bg-gray-200" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-base font-bold text-gray-900">Přiřadit nápad</h2>
            <p className="text-xs text-gray-500 mt-0.5 max-w-[220px] truncate">„{ideaTitle}"</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 rounded-lg p-1 hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          {/* Type selector */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
              Typ touchpointu
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setSelectedType('digital')}
                className={`py-2 px-3 rounded-xl border-2 text-sm font-semibold transition-all
                  ${selectedType === 'digital'
                    ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                    : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300'
                  }`}
              >
                Digitální
              </button>
              <button
                type="button"
                onClick={() => setSelectedType('physical')}
                className={`py-2 px-3 rounded-xl border-2 text-sm font-semibold transition-all
                  ${selectedType === 'physical'
                    ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                    : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300'
                  }`}
              >
                Fyzický
              </button>
            </div>
          </div>

          {/* Phase selector */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
              Fáze
            </label>
            <select
              value={selectedPhaseId}
              onChange={e => { setSelectedPhaseId(e.target.value); setError(''); }}
              className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent transition-all bg-white"
            >
              {phases.map(phase => (
                <option key={phase.id} value={phase.id}>
                  {phase.order}. {phase.name}
                </option>
              ))}
            </select>
            {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
          </div>

          {/* Footer */}
          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 px-4 text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
            >
              Zrušit
            </button>
            <button
              type="submit"
              className="flex-1 flex items-center justify-center gap-2 py-2.5 px-4 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl shadow-sm transition-colors"
            >
              Přiřadit
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AssignIdeaModal;
